// Buttons ordered left to right, row by row.
// Each function may or may not have an inverse function associated with it.
// The inserter functions tell the calculator how to update the expression when a button is used.
// Here we specify the inserter names only. Each inserter must be implemented
// as a method of the Buttons component.

// We render the button in several groups in the DOM,
// and use CSS to visually order them as needed.
export const CONTROLS = 'controls';
export const DIGITS = 'digits';
export const OPERATORS = 'operators';
export const FUNCTIONS = 'functions';
export const CONSTANTS = 'constants';

export const groups = { CONTROLS, DIGITS, OPERATORS, FUNCTIONS, CONSTANTS };

// control buttons
const angleUnits = { group: CONTROLS, symbol: 'angle-units' };
const equals = { group: CONTROLS, symbol: '=' };
const allClear = { group: CONTROLS, symbol: 'AC' };
const inverse = { group: CONTROLS, symbol: 'Inv', inserter: 'invertFunctions', ariaLabel: 'inv' };
// digits
const digit0 = { group: DIGITS, symbol: '0', inserter: 'insertDigit', button: 'outline' };
const digit1 = { group: DIGITS, symbol: '1', inserter: 'insertDigit', button: 'outline' };
const digit2 = { group: DIGITS, symbol: '2', inserter: 'insertDigit', button: 'outline' };
const digit3 = { group: DIGITS, symbol: '3', inserter: 'insertDigit', button: 'outline' };
const digit4 = { group: DIGITS, symbol: '4', inserter: 'insertDigit', button: 'outline' };
const digit5 = { group: DIGITS, symbol: '5', inserter: 'insertDigit', button: 'outline' };
const digit6 = { group: DIGITS, symbol: '6', inserter: 'insertDigit', button: 'outline' };
const digit7 = { group: DIGITS, symbol: '7', inserter: 'insertDigit', button: 'outline' };
const digit8 = { group: DIGITS, symbol: '8', inserter: 'insertDigit', button: 'outline' };
const digit9 = { group: DIGITS, symbol: '9', inserter: 'insertDigit', button: 'outline' };
const decimalSeparator = { group: DIGITS, symbol: 'decimalseparator' };
const leftBracket = { group: DIGITS, symbol: '(', inserter: 'insertDigit', ariaLabel: 'digits.leftparens' };
const rightBracket = { group: DIGITS, symbol: ')', inserter: 'insertDigit', ariaLabel: 'digits.rightparens' };
// operators
const divide = { group: OPERATORS, symbol: '÷', inserter: 'insertWithSpace', button: 'solid-green', ariaLabel: 'operator.divide' };
const multiply = { group: OPERATORS, symbol: '×', inserter: 'insertWithSpace', button: 'solid-green', ariaLabel: 'operator.multiply' };
const subtract = { group: OPERATORS, symbol: '-', inserter: 'insertWithSpace', button: 'solid-green', ariaLabel: 'operator.subtract' };
const add = { group: OPERATORS, symbol: '+', inserter: 'insertWithSpace', button: 'solid-green', ariaLabel: 'operator.add' };
// trigonometric functions
const sin = {
  group: FUNCTIONS,
  symbol: 'sin',
  inserter: 'insertWithBrackets',
  inverseSymbol: 'asin',
  ariaLabel: 'function.sin',
  inverseAriaLabel: 'function.asin',
};
const cos = {
  group: FUNCTIONS,
  symbol: 'cos',
  inserter: 'insertWithBrackets',
  inverseSymbol: 'acos',
  ariaLabel: 'function.cos',
  inverseAriaLabel: 'function.acos',
};
const tan = {
  group: FUNCTIONS,
  symbol: 'tan',
  inserter: 'insertWithBrackets',
  inverseSymbol: 'atan',
  ariaLabel: 'function.tan',
  inverseAriaLabel: 'function.atan',
};
// other functions
const ln = {
  group: FUNCTIONS,
  symbol: 'ln',
  inserter: 'insertWithBrackets',
  inverseSymbol: 'e^',
  inverseInserter: 'insert',
  ariaLabel: 'function.ln',
  inverseAriaLabel: 'function.ex',
};
const log = {
  group: FUNCTIONS,
  symbol: 'log',
  inserter: 'insertWithBrackets',
  inverseSymbol: '10^',
  inverseInserter: 'insert',
  ariaLabel: 'function.log',
  inverseAriaLabel: 'function.tenx',
};
const squareRoot = {
  group: FUNCTIONS,
  symbol: '√',
  inserter: 'insertWithBrackets',
  inverseSymbol: '^2',
  inverseInserter: 'insert',
  ariaLabel: 'function.sqrt',
  inverseAriaLabel: 'function.pow2',
};
const exponent = { group: FUNCTIONS, symbol: 'E', inserter: 'insert', ariaLabel: 'function.exponent' };
const power = { group: FUNCTIONS, symbol: '^', inserter: 'insert', ariaLabel: 'function.power' };
const percent = { group: FUNCTIONS, symbol: '%', inserter: 'insert', ariaLabel: 'function.percent' };
const factorial = { group: FUNCTIONS, symbol: '!', inserter: 'insert', ariaLabel: 'function.factorial' };
// constants
const pi = { group: CONSTANTS, symbol: 'π', inserter: 'insertDigit', ariaLabel: 'constant.pi' };
const euler = { group: CONSTANTS, symbol: 'e', inserter: 'insertDigit', ariaLabel: 'constant.euler' };
const ans = { group: CONSTANTS, symbol: 'Ans', inserter: 'insertDigit', ariaLabel: 'constant.ans' };

// 5 rows of 7 columns.
export const tabletUp = [
  // row 1
  angleUnits,
  factorial,
  leftBracket,
  rightBracket,
  percent,
  allClear,
  // row 2
  inverse,
  sin,
  ln,
  digit7,
  digit8,
  digit9,
  divide,
  // row 3
  pi,
  cos,
  log,
  digit4,
  digit5,
  digit6,
  multiply,
  // row 4
  euler,
  tan,
  squareRoot,
  digit1,
  digit2,
  digit3,
  subtract,
  // row 5
  ans,
  exponent,
  power,
  digit0,
  decimalSeparator,
  equals,
  add,
];

// 5 rows, each with 4 numeric (left) and 3 scientific (right) buttons.
// Initially the numeric side is visible, and a segmented button is used to switch to the right.
export const mobile = [
  // row 1
  //   numeric
  leftBracket,
  rightBracket,
  percent,
  allClear,
  //   scientific
  angleUnits,
  factorial,
  // row 2
  //   numeric
  digit7,
  digit8,
  digit9,
  divide,
  //   scientific
  inverse,
  sin,
  ln,
  // row 3
  //   numeric
  digit4,
  digit5,
  digit6,
  multiply,
  //   scientific
  pi,
  cos,
  log,
  // row 4
  //   numeric
  digit1,
  digit2,
  digit3,
  subtract,
  //   scientific
  euler,
  tan,
  squareRoot,
  // row 5
  //   numeric
  digit0,
  decimalSeparator,
  equals,
  add,
  //   scientific
  ans,
  exponent,
  power,
];
